import type { IPackageManager } from '@wix/package-manager';
import React, { useEffect } from 'react';
import type { FC } from 'react';
import {
  Alert,
  Badge,
  Box,
  ConfirmInput,
  Spinner,
  Text,
} from '@wix/cli-ui-kit';
import { isType, match } from 'variant';
import { FinishedSuccessfullyMessage } from './FinishedSuccessfullyMessage.js';
import type { ProjectData } from '../hooks/create-project.js';
import { useReleaseProject } from '../hooks/release-project.js';

export const ReleaseProject: FC<{
  folderName: string;
  packageManager: IPackageManager;
  targetParentFolder: string;
  projectData: ProjectData;
  siteId: string;
  releaseNonInteractive?: boolean;
}> = ({
  folderName,
  packageManager,
  targetParentFolder,
  projectData,
  siteId,
  releaseNonInteractive,
}) => {
  const { status, execute } = useReleaseProject();

  useEffect(() => {
    if (releaseNonInteractive != null) {
      void execute({
        confirmed: releaseNonInteractive,
        packageManager,
        targetParentFolder,
        folderName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {releaseNonInteractive == null && (
        <Box marginLeft={-2} marginBottom={1}>
          <ConfirmInput
            label={'Would you like to publish your site now?'}
            initialValue={true}
            onSubmit={async (confirmed) => {
              void execute({
                confirmed,
                packageManager,
                targetParentFolder,
                folderName,
              });
            }}
          />
        </Box>
      )}
      {match(status, {
        NotRequested: () => null,
        Success: ({ result }) => {
          return (
            <>
              {match(result, {
                Success: () => (
                  <Box marginLeft={-2}>
                    <Alert type="success">
                      <Badge skin="success">Published</Badge>{' '}
                      <Text>Site published.</Text>
                    </Alert>
                  </Box>
                ),
                Skipped: () => (
                  <Text>
                    {`You can publish your site later by running \`${packageManager.getRunCmd()} release\`.`}
                  </Text>
                ),
                Error: () => (
                  <Box flexDirection="column" gap={1}>
                    <Alert type="error">Failed to publish your site</Alert>
                    <Text>
                      {`You can publish your site later by running \`${packageManager.getRunCmd()} release\`.`}
                    </Text>
                  </Box>
                ),
              })}
              <FinishedSuccessfullyMessage
                packageManager={packageManager}
                folderName={folderName}
                projectData={projectData}
                siteId={siteId}
                isReleased={isType(result, 'Success')}
              />
            </>
          );
        },
        Error: () => null,
        Loading: () => <Spinner text={'Publishing your site...'} />,
      })}
    </>
  );
};
